import { useCallback } from 'react';
import useStorage from '@/shared/hooks/use-storage';
import { postMetricEvent } from '@/shared/services/event-metrics';
import { EVENT_LABELS } from '@/shared/types/event-metrics';
import { createMetricEventPayload } from '@/shared/utils/metrics-helper';
import { reportError, reportWarning } from '@/shared/services/sentry';
import {
  PLANOGRAM_TICKET_VALIDATION_STORAGE_KEY,
  PLANOGRAM_WORK_SESSION_TTL_SECONDS,
} from '@/features/planogram-tasks/constants';
import type { PlanogramReviewSessionTask } from '@/features/planogram-tasks/types';

interface PlanogramTicketValidationState {
  startedAt: number;
  refreshImageCount: number;
}

export interface PublishPlanogramTicketValidationEventParams {
  taskId: number;
  storeId: number;
  shelfId: number;
  origin: PlanogramReviewSessionTask['origin'] | undefined;
  ignored: boolean;
  skipped: boolean;
}

const keyForId = (taskId: number) => `${PLANOGRAM_TICKET_VALIDATION_STORAGE_KEY}-${taskId}`;

const readState = (taskId: number): PlanogramTicketValidationState | null => {
  const raw = sessionStorage.getItem(keyForId(taskId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PlanogramTicketValidationState;
  } catch {
    return null;
  }
};

const writeState = (taskId: number, state: PlanogramTicketValidationState) => {
  sessionStorage.setItem(keyForId(taskId), JSON.stringify(state));
};

/**
 * Clears any ticket-tracking state left behind by a review session that ended without every
 * opened ticket being resolved or skipped - otherwise a ticket reassigned in a later session
 * would inherit the abandoned session's start time and refresh count.
 */
export const clearAllPlanogramTicketValidationState = () => {
  const prefix = `${PLANOGRAM_TICKET_VALIDATION_STORAGE_KEY}-`;
  for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
  }
};

const usePublishPlanogramTicketValidationEvent = () => {
  const { store } = useStorage();

  /**
   * Marks the moment a ticket became the assigned task. Safe to call more than once for the
   * same taskId - it only initializes state when none is stored yet, so it never resets an
   * in-progress ticket's start time or refresh-image count.
   */
  const registerTicketOpened = useCallback((taskId: number) => {
    if (readState(taskId) === null) {
      writeState(taskId, { startedAt: Date.now(), refreshImageCount: 0 });
    }
  }, []);

  /**
   * Counts one successful planogram image refresh for the given ticket. A no-op when the
   * ticket's tracking record is already gone - it was resolved/skipped (and published) while
   * this refresh was still in flight, so there's no active session left to attribute it to.
   */
  const registerImageRefresh = useCallback((taskId: number) => {
    const current = readState(taskId);
    if (current === null) return;
    writeState(taskId, {
      startedAt: current.startedAt,
      refreshImageCount: current.refreshImageCount + 1,
    });
  }, []);

  /**
   * Clears a single ticket's tracking record without publishing a metric. Used when the ticket
   * stops being the assigned task through some path other than resolve/skip (e.g. a refresh
   * request coming back with no assigned task at all), so its stale start time/refresh count
   * can't be inherited if that taskId is assigned again later in the same session.
   */
  const clearTicketValidationState = useCallback((taskId: number) => {
    sessionStorage.removeItem(keyForId(taskId));
  }, []);

  /**
   * Publishes the planogram_ticket_validation_duration_seconds metric for a ticket that just
   * left the assigned-task view, either resolved or skipped.
   */
  const publishTicketValidationEvent = async (params: PublishPlanogramTicketValidationEventParams) => {
    try {
      const state = readState(params.taskId);
      sessionStorage.removeItem(keyForId(params.taskId));

      if (state === null) {
        reportWarning(`No start time found for planogram ticket ${params.taskId}`, {
          feature: 'planogram-tasks',
          action: 'validation_start_time_missing',
        });
        return;
      }

      const duration = Math.round(((Date.now() - state.startedAt) / 1000) * 100) / 100;

      // Should never happen. Ticket tracking is cleared on every work session start/end so this
      // signals leaked tracking state, not a real validation time. Report the real value to
      // Sentry for debugging, but publish the metric capped at the TTL so the leaked state
      // doesn't pollute the metric with an outlier duration.
      let reportedDuration = duration;
      if (duration > PLANOGRAM_WORK_SESSION_TTL_SECONDS) {
        reportWarning(
          `Planogram ticket ${params.taskId} validation duration (${duration}s) exceeds the work session TTL (${PLANOGRAM_WORK_SESSION_TTL_SECONDS}s); publishing capped at the TTL`,
          { feature: 'planogram-tasks', action: 'validation_duration_exceeds_session_ttl' }
        );
        reportedDuration = PLANOGRAM_WORK_SESSION_TTL_SECONDS;
      }

      const authData = await store.getAuth();
      const operator = authData?.user.email || 'unknown_user';

      const tags = {
        store_id: String(params.storeId),
        shelf_id: String(params.shelfId),
        origin: params.origin ?? 'UNKNOWN',
        ignored: String(params.ignored),
        skipped: String(params.skipped),
        refreshImage: String(state.refreshImageCount),
        operator,
      };

      const metricEvent = createMetricEventPayload(
        EVENT_LABELS.PLANOGRAM_TICKET_VALIDATION_DURATION_SECONDS,
        reportedDuration,
        tags
      );
      void postMetricEvent(metricEvent);
    } catch (err: unknown) {
      reportError(err, { feature: 'planogram-tasks', action: 'validation_event_unknown_error' });
    }
  };

  return {
    registerTicketOpened,
    registerImageRefresh,
    clearTicketValidationState,
    publishTicketValidationEvent,
  };
};

export default usePublishPlanogramTicketValidationEvent;
